import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths } from "date-fns";
import { fr } from "date-fns/locale";

export const useProfitComparison = (period: string) => {
  const getPeriodDates = (date: Date) => {
    switch (period) {
      case "day":
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
      case "week":
        return {
          start: startOfWeek(date, { locale: fr }),
          end: endOfWeek(date, { locale: fr })
        };
      case "month":
        return {
          start: startOfMonth(date),
          end: endOfMonth(date)
        };
      default:
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
    }
  };

  const { data } = useQuery({
    queryKey: ["profit_comparison", period],
    queryFn: async () => {
      const now = new Date();
      const currentPeriod = getPeriodDates(now);
      
      let previousDate;
      switch (period) {
        case "day":
          previousDate = subDays(now, 1);
          break;
        case "week":
          previousDate = subWeeks(now, 1);
          break;
        case "month":
          previousDate = subMonths(now, 1);
          break;
        default:
          previousDate = subDays(now, 1);
      }
      const previousPeriod = getPeriodDates(previousDate);

      const fetchPeriodData = async (start: Date, end: Date) => {
        const { data: transactions, error } = await supabase
          .from("poultry_transactions")
          .select("*")
          .gte("created_at", start.toISOString())
          .lte("created_at", end.toISOString());

        if (error) throw error;

        const revenues = transactions
          .filter(tx => tx.transaction_type === "sale")
          .reduce((sum, tx) => sum + tx.total_amount, 0);

        const expenses = transactions
          .filter(tx => tx.transaction_type === "expense")
          .reduce((sum, tx) => sum + tx.total_amount, 0);

        // Add returns to revenues instead of subtracting
        const returns = transactions
          .filter(tx => tx.transaction_type === "return")
          .reduce((sum, tx) => sum + tx.total_amount, 0);

        return {
          revenues: revenues + returns, // Add returns to revenues
          expenses,
          netProfit: revenues + returns - expenses // Include returns in net profit calculation
        };
      };

      const current = await fetchPeriodData(currentPeriod.start, currentPeriod.end);
      const previous = await fetchPeriodData(previousPeriod.start, previousPeriod.end);

      return {
        currentPeriod: current,
        previousPeriod: previous
      };
    }
  });

  return {
    currentPeriod: data?.currentPeriod || { revenues: 0, expenses: 0, netProfit: 0 },
    previousPeriod: data?.previousPeriod || { revenues: 0, expenses: 0, netProfit: 0 }
  };
};